import { useEffect, useRef, useState } from 'react'
import type { MsgDeskPageProps } from '../../MsgDeskApp'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { currentLegacyDeskMode } from '@/msgdeskRuntime/deskNavigation'
import { editorOpenActionForMode } from '../../routing/editor'
import { treeStore } from '@/msgdeskRuntime/state'
import {
  applyStandardMode,
  isStandardEditorPathname,
  pushStandardEditorUrl,
  pushStandardModeUrl,
  replaceStandardModeUrl,
  standardModeFromPathname,
  type StandardRouteMode,
} from '../../host/deskRoutes'
import { standardEditorModeFromSegment, type StandardEditorMode } from '../../routing/standard'
import { WikiPage } from './WikiPage'
import { WikiSharedSurfaceProvider } from './Shared/SharedSurface'
import { WikiPanelsSurfaceProvider } from './panels/PanelsSurface'
import { WikiRowSurfaceProvider } from './rowTemplates/RowSurface'
import { WikiEditorSurfaceProvider } from './editor/EditorSurface'

function routeModeForProjection(mode: string): StandardRouteMode | undefined {
  if (mode === 'msg' || mode === 'answers' || mode === 'submsges' || mode === 'attachments' || mode === 'favs' || mode === 'drafts' || mode === 'trash') return mode
  return undefined
}

export function WikiRoutedPage({
  isEditorOpen = false,
  editorMode,
  editorBridgeReady = false,
  initialMode = 'msg',
  initialEditorMode,
}: MsgDeskPageProps & { initialMode?: StandardRouteMode; initialEditorMode?: StandardEditorMode }) {
  const initialModeSettled = useRef(false)
  const initialModeAttempts = useRef(0)
  const initialEditorOpened = useRef(false)
  const editorWasOpen = useRef(false)
  const [initialModeTick, setInitialModeTick] = useState(0)
  const [initialRouteReady, setInitialRouteReady] = useState(initialMode === 'msg')
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    if (initialModeSettled.current || initialMode === 'msg') {
      initialModeSettled.current = true
      setInitialRouteReady(true)
      return
    }
    if (!editorBridgeReady) return

    const currentState = treeStore.getState()
    if (currentState.subsLoading) return
    const matchesRoute = initialMode === 'attachments'
      ? currentState.renderProjection.mode === 'attachments'
      : currentLegacyDeskMode() === initialMode
    const timer = window.setTimeout(() => {
      if (matchesRoute) {
        initialModeSettled.current = true
        setInitialRouteReady(true)
        return
      }
      if (initialModeAttempts.current >= 100) return
      initialModeAttempts.current += 1
      applyStandardMode(initialMode)
      setInitialModeTick(value => value + 1)
    }, matchesRoute ? 300 : 100)

    return () => window.clearTimeout(timer)
  }, [editorBridgeReady, initialMode, initialModeTick])

  useEffect(() => {
    if (!initialEditorMode || !editorBridgeReady || !initialRouteReady || initialEditorOpened.current) return
    initialEditorOpened.current = true
    dispatchEditorAction({ action: editorOpenActionForMode(initialEditorMode) })
  }, [editorBridgeReady, initialEditorMode, initialRouteReady])

  useEffect(() => {
    if (isEditorOpen) {
      editorWasOpen.current = true
      if (editorMode === 'new_msg' || editorMode === 'update_msg') pushStandardEditorUrl(editorMode)
      return
    }
    setSettingsOpen(false)
    if (editorWasOpen.current && isStandardEditorPathname(window.location.pathname)) {
      editorWasOpen.current = false
      replaceStandardModeUrl('msg')
    }
  }, [editorMode, isEditorOpen])

  useEffect(() => treeStore.subscribe(() => {
    if (!initialModeSettled.current) return
    if (initialEditorMode && !initialEditorOpened.current) return
    if (treeStore.getState().renderProjection.mode === 'editor') return
    const mode = routeModeForProjection(treeStore.getState().renderProjection.mode)
    if (mode) pushStandardModeUrl(mode)
  }), [initialEditorMode])

  useEffect(() => {
    const handlePopState = () => {
      const segment = window.location.pathname.split('/').filter(Boolean).pop() || ''
      const routeEditorMode = standardEditorModeFromSegment(segment)
      if (routeEditorMode && editorBridgeReady) {
        dispatchEditorAction({ action: editorOpenActionForMode(routeEditorMode) })
        return
      }
      applyStandardMode(standardModeFromPathname(window.location.pathname))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [editorBridgeReady])

  return (
    <WikiSharedSurfaceProvider>
      <WikiRowSurfaceProvider>
        <WikiPanelsSurfaceProvider>
          <WikiEditorSurfaceProvider>
            <WikiPage
              isEditorOpen={isEditorOpen}
              editorMode={editorMode}
              settingsOpen={settingsOpen}
              onToggleSettings={() => setSettingsOpen(open => !open)}
              onCloseSettings={() => setSettingsOpen(false)}
            />
          </WikiEditorSurfaceProvider>
        </WikiPanelsSurfaceProvider>
      </WikiRowSurfaceProvider>
    </WikiSharedSurfaceProvider>
  )
}
